import { AnimatedCounter, NavyAtmosphere, ScrollReveal, SplitReveal, TextStagger } from '@/components/motion';
import { ACHIEVEMENT_HEADLINE } from '@/data/experience';

/**
 * AchievementHero (/experience/student-achievement-rates — page hero) — the navy
 * opener for the ACBSP-mandated dedicated achievement page. A back-context
 * eyebrow and a short uppercase kicker sit above a SplitReveal headline and a
 * lede that frames the figures below as ACBSP-accredited MBA & DBA data,
 * institution-reported for 2021–2025 and published in full. The four
 * ACHIEVEMENT_HEADLINE figures follow as glass-on-navy cards with count-up
 * numbers, each carrying its year/scope caption.
 *
 * Motion: TextStagger kicker, SplitReveal headline, ScrollReveal child-stagger
 * for the stat row, AnimatedCounter for every figure (animates on view over a
 * NavyAtmosphere backdrop). All primitives are reduced-motion safe and never
 * strand content hidden.
 *
 * Sourcing: every number and caption is read verbatim from ACHIEVEMENT_HEADLINE
 * in '@/data/experience'; nothing is invented, reworded or re-numbered. White /
 * white-70 / gold-light text on navy keeps contrast compliant.
 */
export function AchievementHero() {
  return (
    <section className="relative isolate overflow-hidden bg-brand-navy">
      <NavyAtmosphere />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-20 md:py-28">
        <div className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold-light uppercase">
            IBAS Experience · Student achievement
          </p>
          <TextStagger
            text="ACBSP-accredited · 2021–2025"
            className="mt-4 block font-label text-[0.7rem] tracking-[0.24em] text-white/50 uppercase"
          />
          <SplitReveal
            as="h1"
            by="lines"
            className="mt-5 font-display text-4xl leading-[1.05] text-white md:text-6xl"
          >
            Student Achievement Rates.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">
              These are our ACBSP-accredited MBA and DBA achievement figures, reported by the
              institution for 2021–2025. In keeping with our accreditation, they are published here
              in full — retention, graduate cohorts and learning-outcome achievement, set out below.
            </p>
          </ScrollReveal>
        </div>

        {/* ── Headline count-up figures ─────────────────────────────────── */}
        <ScrollReveal
          stagger={0.08}
          className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {ACHIEVEMENT_HEADLINE.map((stat) => (
            <div
              key={stat.label}
              className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/40 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-brand-gold-light/10 text-brand-gold-light">
                <stat.Icon aria-hidden className="size-5" />
              </div>
              <AnimatedCounter
                value={stat.value}
                decimals={stat.decimals ?? 0}
                suffix={stat.suffix}
                grouping
                className="mt-5 block font-display text-4xl leading-none font-semibold text-white md:text-5xl"
              />
              <p className="mt-3 text-sm leading-snug font-medium text-white">{stat.label}</p>
              <p className="mt-1 text-xs leading-snug text-white/60">{stat.caption}</p>
            </div>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}
